import { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy, doc, setDoc, getDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const MomentumContext = createContext();

export function MomentumProvider({ children }) {
  const { currentUser } = useAuth();
  const [streak, setStreak] = useState(0);
  const [lastActive, setLastActive] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch and calculate streak
  useEffect(() => {
    if (!currentUser) {
      setStreak(0);
      setLastActive(null);
      setLoading(false);
      return;
    }

    const fetchMomentum = async () => {
      try {
        // Get user's momentum document
        const momentumRef = doc(db, 'momentum', currentUser.uid);
        const momentumSnap = await getDoc(momentumRef);
        
        // Get recent goal updates
        const today = new Date();
        const sevenDaysAgo = new Date(today.setDate(today.getDate() - 7));
        
        const goalsRef = collection(db, 'goals');
        const q = query(
          goalsRef,
          where('userId', '==', currentUser.uid),
          where('updatedAt', '>=', sevenDaysAgo.toISOString()),
          orderBy('updatedAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const activityDays = new Set();
        
        querySnapshot.docs.forEach(doc => {
          const date = new Date(doc.data().updatedAt).toDateString();
          activityDays.add(date);
        });

        // Calculate current streak
        let currentStreak = 0;
        let checkDate = new Date();
        
        while (
          currentStreak < 7 && 
          activityDays.has(checkDate.toDateString())
        ) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        }

        // If there's existing momentum data, use it for comparison
        if (momentumSnap.exists()) {
          const momentumData = momentumSnap.data();
          const lastActiveDate = new Date(momentumData.lastActive);
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);

          // If last active was before yesterday, reset streak
          if (lastActiveDate < yesterday) {
            currentStreak = activityDays.has(new Date().toDateString()) ? 1 : 0;
          }
        }

        setStreak(currentStreak);
        setLastActive(new Date().toISOString());
        
        // Update momentum document
        await setDoc(momentumRef, {
          streak: currentStreak,
          lastActive: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }, { merge: true });

      } catch (error) {
        console.error('Error fetching momentum:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMomentum();
  }, [currentUser]);

  return (
    <MomentumContext.Provider value={{ 
      streak,
      lastActive,
      loading
    }}>
      {children}
    </MomentumContext.Provider>
  );
}

export function useMomentum() {
  return useContext(MomentumContext);
} 